import { z } from 'zod';
import { accountStatusSchema, roleSchema } from './auth';

export const accountSchema = z.object({
  id: z.uuid(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  roles: z.array(roleSchema),
  userId: z.string().nullable(),
  organizationId: z.uuid().nullable(),
  status: accountStatusSchema.nullable(),
  timezone: z.string(),
  paymentZoneId: z.uuid().nullable(),
  createdOn: z.string().nullable(),
  modifiedOn: z.string().nullable(),
  deletedOn: z.string().nullable(),
});

export type Account = z.infer<typeof accountSchema>;

// Legacy USER_NOT_REGISTERED / TP-3628 / TCA15-3600. Invited accounts must
// not receive a password-reset email — even when `create` already linked a
// Clerk userId before the user registered.
export const USER_NOT_REGISTERED_MESSAGE =
  'This user has been invited but has not registered yet. Password reset is only available for registered users.';

export const createAccountInput = z.object({
  firstName: z.string().min(1),
  lastName: z.string().min(1),
  email: z.email(),
  phone: z.string().optional(),
  roles: z.array(roleSchema).default(['consulting']),
  organizationId: z.uuid(),
  timezone: z.string().default('UTC'),
});

export type CreateAccountInput = z.infer<typeof createAccountInput>;

export const updateAccountInput = z.object({
  id: z.uuid(),
  firstName: z.string().min(1).optional(),
  lastName: z.string().min(1).optional(),
  email: z.email().optional(),
  phone: z.string().optional(),
  timezone: z.string().optional(),
  roles: z.array(roleSchema).optional(),
  status: accountStatusSchema.optional(),
  organizationId: z.uuid().optional(),
});

export type UpdateAccountInput = z.infer<typeof updateAccountInput>;

export const switchOrganizationInput = z.object({
  organizationId: z.uuid(),
});

export type SwitchOrganizationInput = z.infer<typeof switchOrganizationInput>;

export const findAccountsInput = z.object({
  offset: z.number().int().nonnegative().default(0),
  limit: z.number().int().positive().max(100).default(20),
  search: z.string().optional(),
  organizationId: z.uuid().optional(),
  groupId: z.uuid().optional(),
  // When set and no groupId is provided, restrict results to members of the
  // org's sales Teams only (used by the consultant picker / moodboard).
  salesTeamsOnly: z.boolean().optional(),
});

export type FindAccountsInput = z.infer<typeof findAccountsInput>;

// Minimal account shape returned by `accounts.listForFilters` — the
// non-admin endpoint used by the itineraries Owner / Collaborators
// MultiSelects. Returns only `id + fullName` so consultants can pick
// from the org without leaking emails / roles / payment info that the
// full `accountSchema` exposes (those need admin via the legacy
// `accounts.list`).
export const accountFilterOption = z.object({
  id: z.uuid(),
  fullName: z.string(),
});

export type AccountFilterOption = z.infer<typeof accountFilterOption>;

export const countryGroupSchema = z.object({
  id: z.uuid(),
  title: z.string(),
});

export type CountryGroup = z.infer<typeof countryGroupSchema>;

export const listCountryGroupsInput = z.object({
  organizationId: z.uuid(),
});

export type ListCountryGroupsInput = z.infer<typeof listCountryGroupsInput>;

// Team groups (group_type='Team') for the moodboard team selector. Shares
// the `{ id, title }` shape of country groups. `listTeamGroups` is
// role-aware: admins see all teams under the given country; consultants
// see only the teams they belong to (optionally narrowed to a country).
export const teamGroupSchema = z.object({
  id: z.uuid(),
  title: z.string(),
});

export type TeamGroup = z.infer<typeof teamGroupSchema>;

export const listTeamGroupsInput = z.object({
  organizationId: z.uuid(),
  countryId: z.uuid().optional(),
});

export type ListTeamGroupsInput = z.infer<typeof listTeamGroupsInput>;

export const createAllowlistInput = z.object({
  emails: z.array(z.email()).min(1),
  roles: z.array(roleSchema).min(1),
  sendInvite: z.boolean().default(true),
  timezone: z.string().default('UTC'),
});

export type CreateAllowlistInput = z.infer<typeof createAllowlistInput>;

export const accountSortField = z.enum(['name', 'email', 'roles', 'status']);

export type AccountSortField = z.infer<typeof accountSortField>;

export const allowlistFiltersInput = z.object({
  offset: z.number().int().nonnegative().default(0),
  limit: z.number().int().positive().max(100).default(20),
  search: z.string().optional(),
  // Legacy parity (account.service.ts:406-412): allowlist UI can filter
  // by status (e.g. show only `Invited`) and by intended role.
  status: z.array(accountStatusSchema).optional(),
  roles: z.array(roleSchema).optional(),
  // Org scope. The router injects `ctx.user.organizationId` here for
  // non-system admins (legacy `WHERE account.organizationId = :org`).
  // Declared so the value survives input validation and reaches the
  // service — without it the router's spread is silently dropped.
  organizationId: z.uuid().optional(),
  // Soft-delete scope. `remove` stamps `deletedOn` + `status='Suspended'`
  // together, so deleted rows are excluded by DEFAULT and the caller opts in
  // explicitly to see them. Suspension is the only producer of deleted rows
  // today and un-suspending happens from this table, so the Accounts UI
  // passes `true` — it is the default that protects every other consumer.
  includeDeleted: z.boolean().optional(),
  sortBy: accountSortField.optional(),
  sortOrder: z.enum(['asc', 'desc']).optional(),
});

export type AllowlistFiltersInput = z.infer<typeof allowlistFiltersInput>;

export const restoreAccountInput = z.object({
  id: z.uuid(),
});

export type RestoreAccountInput = z.infer<typeof restoreAccountInput>;

export const allowlistSkippedEntrySchema = z.object({
  email: z.string(),
  reason: z.string(),
});

export type AllowlistSkippedEntry = z.infer<typeof allowlistSkippedEntrySchema>;

export const createAllowlistResultSchema = z.object({
  accounts: z.array(accountSchema),
  skipped: z.array(allowlistSkippedEntrySchema),
});

export type CreateAllowlistResult = z.infer<typeof createAllowlistResultSchema>;

export const loginDiagnosticsInput = z.object({
  id: z.uuid(),
});

export type LoginDiagnosticsInput = z.infer<typeof loginDiagnosticsInput>;

/**
 * Everything an admin needs to answer "why can't this user log in?" (XT-716).
 *
 * The Clerk-derived flags are nullable on purpose: `clerkReachable: false`
 * means Clerk was never asked, and an unknown must not be rendered as a
 * negative — telling an admin "locked: no" when nobody checked sends them
 * down exactly the wrong path.
 */
export const loginDiagnosticsSchema = z.object({
  status: accountStatusSchema.nullable(),
  roles: z.array(roleSchema),
  organizationId: z.uuid().nullable(),
  deletedOn: z.string().nullable(),
  // Set while an admin-issued password is still awaiting its replacement.
  mustChangePassword: z.boolean(),
  hasClerkUser: z.boolean(),
  // Clerk answered, and the `userId` on this row is not a user it knows —
  // distinct from an outage, and a blocking cause in its own right.
  clerkUserMissing: z.boolean(),
  clerkReachable: z.boolean(),
  lastSignInAt: z.string().nullable(),
  lastActiveAt: z.string().nullable(),
  banned: z.boolean().nullable(),
  locked: z.boolean().nullable(),
  lastPasswordResetAt: z.string().nullable(),
});

export type LoginDiagnostics = z.infer<typeof loginDiagnosticsSchema>;
